// ---- Quest state (client) ----
// The client mirror of the server's authoritative per-player quest progress. The server
// streams a personal `{ t:"quests", quests, kills }` message (net.ts) whenever our
// progress changes; we store it here, joined against the static QUESTS registry for all
// the text. talk.ts reads `condMet` to gate quest dialogue; journal.ts reads the getters
// to render the book. We also keep an ordered diary (`journalLog`) by edge-detecting
// step transitions as updates arrive — one entry logged each time a step becomes active.

import type { ItemType, NetQuest } from "./shared.ts";
import type { TalkCond } from "./npc/define.ts";
import { QUESTS, questById } from "./quest/index.ts";
import type { QuestSpec } from "./quest/define.ts";
import { countOf } from "./inventory.ts";

const progress = new Map<string, NetQuest>(); // id → latest progress
let kills = 0;

export type JournalEntry = { questId: string; questTitle: string; text: string };
const journalLog: JournalEntry[] = [];

/** A quest joined with its definition + per-step done/active/future flags, for the HUD. */
export type QuestView = {
  spec: QuestSpec;
  net: NetQuest;
  steps: { id: string; desc: string; state: "done" | "active" | "future" }[];
};

function view(net: NetQuest): QuestView | null {
  const spec = questById(net.id);
  if (!spec) return null;
  const steps = spec.steps.map((s, i) => ({
    id: s.id,
    desc: s.desc,
    state:
      net.status === "completed" || i < net.stepIndex
        ? ("done" as const)
        : i === net.stepIndex
          ? ("active" as const)
          : ("future" as const),
  }));
  return { spec, net, steps };
}

/** Apply a personal quest update from the server, logging any newly-reached steps. */
export function setQuests(list: NetQuest[], killCount: number): void {
  kills = killCount;
  const held = new Set<string>();
  for (const next of list) {
    held.add(next.id);
    const spec = questById(next.id);
    if (!spec) continue;
    const prev = progress.get(next.id);

    // Fresh quest (first update after a page load): the server persisted only the
    // CURRENT step, so replay the whole diary up to it — otherwise reloading mid-quest
    // silently drops steps 0..n-1's notes, and a quest finished last session shows none.
    // Dev scrubber rewinds (stepIndex drops / completed → active) also rebuild the
    // diary so a journal restart doesn't leave stale "Completed" lines around.
    const rewound = !!prev && (
      next.stepIndex < prev.stepIndex
      || (prev.status === "completed" && next.status === "active")
    );
    if (!prev || rewound) {
      if (rewound) {
        for (let i = journalLog.length - 1; i >= 0; i--) {
          if (journalLog[i]!.questId === next.id) journalLog.splice(i, 1);
        }
      }
      const upTo = next.status === "completed" ? spec.steps.length - 1 : next.stepIndex;
      for (let i = 0; i <= upTo && i < spec.steps.length; i++) {
        journalLog.push({ questId: spec.id, questTitle: spec.title, text: spec.steps[i]!.journal });
      }
    } else if (next.stepIndex > prev.stepIndex) {
      // Advanced → log the entry for each step we moved into (usually one).
      for (let i = prev.stepIndex + 1; i <= next.stepIndex && i < spec.steps.length; i++) {
        journalLog.push({ questId: spec.id, questTitle: spec.title, text: spec.steps[i]!.journal });
      }
    }
    // Just completed → a closing line.
    if (next.status === "completed" && prev?.status !== "completed") {
      journalLog.push({ questId: spec.id, questTitle: spec.title, text: `Completed: ${spec.title}.` });
    }
    progress.set(next.id, next);
  }
  // Server list is authoritative — drop quests no longer held (dev Unstart) and
  // scrub their diary so offer dialogue + journal match "never started".
  for (const id of [...progress.keys()]) {
    if (held.has(id)) continue;
    progress.delete(id);
    for (let i = journalLog.length - 1; i >= 0; i--) {
      if (journalLog[i]!.questId === id) journalLog.splice(i, 1);
    }
  }
}

/** Evaluate a dialogue choice's quest condition against our current state
 *  (and optional bag `has` gate). */
export function condMet(cond: TalkCond): boolean {
  const p = progress.get(cond.quest);
  if (cond.is === "unstarted") {
    if (p) return false;
  } else {
    if (!p) return false;
    if (cond.is === "completed") {
      if (p.status !== "completed") return false;
    } else {
      // is: "active"
      if (p.status !== "active") return false;
      if (cond.step !== undefined) {
        const spec = questById(cond.quest);
        if (!spec || spec.steps[p.stepIndex]?.id !== cond.step) return false;
      }
    }
  }
  // Bag gate (deliver turn-ins): OR-sum across types, same as bagHasAny server-side.
  if (cond.has) {
    const need = cond.has.count ?? 1;
    let have = 0;
    for (const t of cond.has.types) have += countOf(t as ItemType);
    if (have < need) return false;
  }
  return true;
}

/** Active (not-yet-completed) quests, joined with their definitions, for the journal. */
export function activeQuests(): QuestView[] {
  const out: QuestView[] = [];
  for (const q of QUESTS) {
    const p = progress.get(q.id);
    if (p && p.status === "active") {
      const v = view(p);
      if (v) out.push(v);
    }
  }
  return out;
}

/** The player's current quest waypoint — the first active quest's current step.
 *  Drives the top-center tracker, the world marker over the target NPC, and the
 *  journal's "current objective" callout. null when nothing is underway. */
export type QuestWaypoint = {
  questId: string;
  questTitle: string;
  stepId: string;
  /** Objective line (e.g. "Talk to Dekkar on the dock"). */
  desc: string;
  /** NPC SPAWN id for talk/kill/deliver steps; null for item/custom / no world target. */
  targetNpcId: string | null;
};

/** First active quest's live step, or null. Order follows the QUESTS registry. */
export function currentWaypoint(): QuestWaypoint | null {
  for (const q of QUESTS) {
    const p = progress.get(q.id);
    if (!p || p.status !== "active") continue;
    const step = q.steps[p.stepIndex];
    if (!step) continue;
    const obj = step.objective;
    const targetNpcId =
      obj.kind === "talk" || obj.kind === "kill" || obj.kind === "deliver" ? obj.npcId : null;
    return {
      questId: q.id,
      questTitle: q.title,
      stepId: step.id,
      desc: step.desc,
      targetNpcId,
    };
  }
  return null;
}

/** Completed quests, joined with their definitions, for the journal. */
export function completedQuests(): QuestView[] {
  const out: QuestView[] = [];
  for (const q of QUESTS) {
    const p = progress.get(q.id);
    if (p && p.status === "completed") {
      const v = view(p);
      if (v) out.push(v);
    }
  }
  return out;
}

/** The diary entries, oldest first. */
export function journalEntries(): JournalEntry[] {
  return journalLog;
}

/** Server-tracked goblins-slain tally (for the About Me page). */
export function killCount(): number {
  return kills;
}
